import {
  Grid,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Button,
  InputLabel,
} from '@mui/material';
import { useMemo, useState } from 'react';

/* =======================
   TIPOS
======================= */

export interface DireccionCoordenadas {
  local?: string;
  id_coordenada?: string;
  id_coordenada_02?: string;
  id_coordenada_03?: string;
  id_coordenada_04?: string;
  id_coordenada_05?: string;
  id_coordenada_06?: string;
  id_coordenada_07?: string;
  id_coordenada_08?: string;
  id_coordenada_09?: string;
  complemento1?: string;
  complemento2?: string;

}

interface CatalogoItem {
  code: string;
  value: string;
}



interface AddressControlsProps {
  direccion_coordenadas?: string;
  direccion?: DireccionCoordenadas;
  disabled?: boolean;
  titulo?: string;

  onDireccionChange: (direccion: DireccionCoordenadas) => void;
  onValueChange: (value: string) => void;
  onDireccionCoordenadasChange: (value: string) => void;

  eventOnKeyDownInputAlphaNumeric?: (e: any) => void;
  eventOnKeyDownInputNumber?: (e: any) => void;
  listViaPrincipalComplementoDireccion?: CatalogoItem[];
  listViaPrincipalEmpresa?: CatalogoItem[];
  listViaPrincipalCuadrante?: CatalogoItem[];
}

/* =======================
   LISTAS
======================= */


const listViaPrincipalPrimeraLetra: CatalogoItem[] = [
  { code: 'A', value: 'A' },
  { code: 'B', value: 'B' },
  { code: 'C', value: 'C' },
  { code: 'D', value: 'D' },
  { code: 'E', value: 'E' },
  { code: 'F', value: 'F' },
  { code: 'G', value: 'G' },
  { code: 'H', value: 'H' },
  { code: 'I', value: 'I' },
  { code: 'J', value: 'J' },
  { code: 'K', value: 'K' },  
  { code: 'L', value: 'L' },
  { code: 'M', value: 'M' },
  { code: 'N', value: 'N' },
  { code: 'O', value: 'O' },
  { code: 'P', value: 'P' },  
  { code: 'Q', value: 'Q' },
  { code: 'R', value: 'R' },
  { code: 'S', value: 'S' },
  { code: 'T', value: 'T' },
  { code: 'U', value: 'U' },
  { code: 'V', value: 'V' },
  { code: 'W', value: 'W' },
  { code: 'X', value: 'X' },
  { code: 'Y', value: 'Y' },
  { code: 'Z', value: 'Z' }
];

const viaPrincipalBisList: CatalogoItem[] = [
  { code: 'BIS', value: 'BIS' },
];



/* =======================
   HELPERS
======================= */

const safe = (v?: string) => (v ? v.trim() : '');

const buildDireccion = (d?: DireccionCoordenadas): string =>
  d
    ? [
        d.local,
        d.id_coordenada,
        d.id_coordenada_02,
        d.id_coordenada_03,
        d.id_coordenada_04,
        d.id_coordenada_05,
        d.id_coordenada_06,
        d.id_coordenada_07,
        d.id_coordenada_08,
        d.id_coordenada_09,
        d.complemento1,
        d.complemento2,
      ]
        .map(safe)
        .filter(Boolean)
        .join(' ')
    : '';

const buildDireccionCoordenadasPipe = (d?: DireccionCoordenadas): string =>
  [
    d?.local,
    d?.id_coordenada,
    d?.id_coordenada_02,
    d?.id_coordenada_03,
    d?.id_coordenada_04,
    d?.id_coordenada_05,
    d?.id_coordenada_06,
    d?.id_coordenada_07,
    d?.id_coordenada_08,
    d?.id_coordenada_09,
    d?.complemento1,
    d?.complemento2,
  ]
    .map(v => v ?? '')
    .join('|');

/* =======================
   COMPONENTE
======================= */

export const AddressControls = ({
  direccion_coordenadas,
  direccion,
  disabled,
  titulo,
  onDireccionChange,
  onValueChange,
  onDireccionCoordenadasChange,
  eventOnKeyDownInputAlphaNumeric,
  eventOnKeyDownInputNumber,
  listViaPrincipalComplementoDireccion,
  listViaPrincipalEmpresa,
  listViaPrincipalCuadrante
}: AddressControlsProps) => {

  /* ====== Parseo SOLO para pintar ====== */
  const parsedDireccion = useMemo<DireccionCoordenadas | undefined>(() => {
    if (!direccion_coordenadas) return undefined;

    const p = direccion_coordenadas.split('|');
    console.log('Parsed DireccionCoordenadas:', p);
    return {
      local:            p[0] ?? '',
      id_coordenada:    p[1] ?? '',
      id_coordenada_02: p[2] ?? '',
      id_coordenada_03: p[3] ?? '',
      id_coordenada_04: p[4] ?? '',
      id_coordenada_05: p[5] ?? '',
      id_coordenada_06: p[6] ?? '',
      id_coordenada_07: p[7] ?? '',
      id_coordenada_08: p[8] ?? '',
      id_coordenada_09: p[9] ?? '',
      complemento1:     p[10] ?? '',
      complemento2:     p[11] ?? '',
    };
  }, [direccion_coordenadas]);

  /* ====== Dirección efectiva ====== */
  const direccionActual = direccion ?? parsedDireccion;

  /* ====== Update SOLO por interacción ====== */
  const update = (field: keyof DireccionCoordenadas, val: string) => {
    const base = direccionActual ?? {};
    const nuevaDireccion: DireccionCoordenadas = {
      ...base,
      [field]: val,
    };

    onDireccionChange(nuevaDireccion);
    onValueChange(buildDireccion(nuevaDireccion));
    onDireccionCoordenadasChange(
      buildDireccionCoordenadasPipe(nuevaDireccion)
    );
  };

  const handleAlphaNumeric = (e: any) => {
    if (!/^[a-zA-Z0-9\s]$/.test(e.key) && e.key !== 'Backspace') {
      e.preventDefault();
    }
  };

  const handleNumberOnly = (e: any) => {
    if (!/[0-9]/.test(e.key) && e.key !== 'Backspace') {
      e.preventDefault();
    }
  };



const [showComplemento, setShowComplemento] = useState(
  !!(direccionActual?.complemento1 || direccionActual?.complemento2)
);

const handleAddComplemento = () => {
  setShowComplemento(true);
};

const handleDelComplemento = (index: any) => {
  const base = direccionActual ?? {};
  const nuevaDireccion: DireccionCoordenadas = {
    ...base,
    complemento1: '',
    complemento2: '',
  };
  onDireccionChange(nuevaDireccion);
  onValueChange(buildDireccion(nuevaDireccion));
  onDireccionCoordenadasChange(buildDireccionCoordenadasPipe(nuevaDireccion));
  setShowComplemento(false);
};


  return (
    <div>
      <label className='fw-bold m-1'>{titulo}</label>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid xs={12} sm={6} md={2} item>
          <FormControl fullWidth size="small">
            <Select size="small"
              value={direccionActual?.local ?? ''}
              disabled={disabled}
              onChange={(e) => update('local', e.target.value)}
            >
              <MenuItem value="">Seleccione...</MenuItem>
              {listViaPrincipalEmpresa?.map(({ code, value }) => (
                <MenuItem key={code} value={code}>{value}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6} md={2} item>
          <TextField
            fullWidth size="small"
            value={direccionActual?.id_coordenada ?? ''}
            onKeyDown={eventOnKeyDownInputAlphaNumeric ?? handleAlphaNumeric}
            onChange={(e) => update('id_coordenada', e.target.value)}
          />
        </Grid>

        <Grid xs={12} sm={6} md={2} item>
          <FormControl fullWidth size="small">
            <Select size="small"
              value={direccionActual?.id_coordenada_02 ?? ''}
              onChange={(e) => update('id_coordenada_02', e.target.value)}
            >
              <MenuItem value="">Seleccione...</MenuItem>
              {listViaPrincipalPrimeraLetra.map(({ code, value }) => (
                <MenuItem key={code} value={code}>{value}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6} md={2} item>
          <FormControl fullWidth size="small">
            <Select size="small"
              value={direccionActual?.id_coordenada_03 ?? ''}
              onChange={(e) => update('id_coordenada_03', e.target.value)}
            >
              <MenuItem value="">Seleccione...</MenuItem>
              {viaPrincipalBisList.map(({ code, value }) => (
                <MenuItem key={code} value={code}>{value}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6} md={2} item>
          <FormControl fullWidth size="small">
            <Select size="small"
              value={direccionActual?.id_coordenada_04 ?? ''}
              onChange={(e) => update('id_coordenada_04', e.target.value)}
            >
              <MenuItem value="">Seleccione...</MenuItem>
              {listViaPrincipalPrimeraLetra.map(({ code, value }) => (
                <MenuItem key={code} value={code}>{value}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6} md={2} item>
          <FormControl fullWidth size="small">
            <Select size="small"
              value={direccionActual?.id_coordenada_05 ?? ''}
              onChange={(e) => update('id_coordenada_05', e.target.value)}
            >
              <MenuItem value="">Seleccione...</MenuItem>
              {listViaPrincipalCuadrante?.map(({ code, value }) => (
                <MenuItem key={code} value={code}>{value}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={2} sm={1} md={0.5} display="flex" justifyContent="center" alignItems="center">#</Grid>
        <Grid xs={12} sm={6} md={2} item>
          <TextField
            fullWidth size="small"
            value={direccionActual?.id_coordenada_06 ?? ''}
            onKeyDown={eventOnKeyDownInputNumber ?? handleNumberOnly}
            onChange={(e) =>
              update('id_coordenada_06', e.target.value.slice(0, 4))
            }
          />
        </Grid>
        <Grid xs={12} sm={6} md={2} item>
          <FormControl sx={{ minWidth: '100%' }} size='small'>
            
            <Select size="small"
              value={direccionActual?.id_coordenada_07 ?? ''}
              onChange={(e) => update('id_coordenada_07', e.target.value)}
            >
              <MenuItem value="">Seleccione...</MenuItem>
              {listViaPrincipalPrimeraLetra.map(({ code, value }) => (
                <MenuItem key={code} value={code}>{value}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={2} sm={1} md={0.5} display="flex" justifyContent="center" alignItems="center">-</Grid>

        <Grid xs={12} sm={6} md={2} item>
          <TextField
            fullWidth size="small"
            value={direccionActual?.id_coordenada_08 ?? ''}
            onKeyDown={eventOnKeyDownInputNumber ?? handleNumberOnly}
            onChange={(e) =>
              update('id_coordenada_08', e.target.value.slice(0, 4))
            }
          />
        </Grid>
      
        <Grid xs={12} sm={6} md={2} item>

          <FormControl sx={{ minWidth: '100%' }} size='small'> 
            <Select 
              labelId='id_coordenada_09'
              id='id_coordenada_09'
              label=''
              size='small'
              value={direccionActual?.id_coordenada_09 ?? ''}
                onChange={(e) =>
                  update('id_coordenada_09', e.target.value)
              }
            >
              <MenuItem value="">Seleccione...</MenuItem>
              {listViaPrincipalCuadrante?.map(({ code, value }) => (
                <MenuItem key={code} value={code}>{value}</MenuItem>
              ))}
            </Select>   
          </FormControl>
        </Grid>

       

        <Grid xs={12} pr={2} item={true}>
          <Grid container>
            <Grid container direction='row' item={true} justifyContent="space-between">

              {/* Select + TextField — solo visibles si showComplemento es true */}
              {showComplemento && (
                <>
                  <Grid item xs={12} sm={4} md={2} mt={2}>
                    <FormControl sx={{ minWidth: '100%' }} size='medium'>
                      <InputLabel id="tipo_complemento_notificacion">Tipo de complemento</InputLabel>
                      <Select
                        labelId='tipo_complemento_notificacion'
                        id='tipo_complemento_notificacion'
                        label='Tipo de complemento'
                        value={direccionActual?.complemento1 ?? ''}
                        onChange={(e) => update('complemento1', e.target.value)}
                      >
                        <MenuItem value='' key=''>Seleccione...</MenuItem>
                        {listViaPrincipalComplementoDireccion?.map(({ code, value }) => (
                          <MenuItem key={code} value={code}>{value}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={4} md={2} mt={2}>
                    <FormControl sx={{ minWidth: '100%' }} size='small'>
                      <TextField
                        fullWidth size='medium'
                        value={direccionActual?.complemento2 ?? ''}                
                        id='complemento2'
                        name='complemento2'
                        label='Complemento notificación'
                        inputProps={{ maxLength: 100 }}
                        onChange={(e) => update('complemento2', e.target.value.slice(0, 100))}
                      />
                    </FormControl>
                  </Grid>
                </>
              )}

              {/* Botón — cambia según showComplemento */}
              <Grid item xs={12} sm={4} md={2} mt={2}>
                {!showComplemento ? (
                  <Button
                    fullWidth
                    variant='contained'
                    onClick={handleAddComplemento}
                    sx={{ height: 50 }}
                  >
                    Nuevo complemento +
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    variant='contained'
                    onClick={() => handleDelComplemento('')}
                    style={{ width: '100%', height: '50px' }}
                  >
                    Excluir complemento -
                  </Button>
                )}
              </Grid>

            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};
